import PropTypes from 'prop-types';
import { useState, useCallback, useEffect } from 'react';

import Button from '../Button';
import AddIcon from '../Icons/AddIcon';
import Spinner from '../Spinner';
import EstimateFormItem from './EstimateFormItem';
import * as S from './styled';

const EstimateForm = (props) => {
  const { userId, boardId, estimates } = props;
  const [isAddNew, setIsAddNew] = useState(false);
  const [editModeId, setEditModeId] = useState(null);
  const isLoading = [
    props.isEstimatesLoading,
    props.isUsersLoading,
    props.deleteResult.isLoading,
    props.isSaving,
  ].some(Boolean);

  useEffect(() => {
    // if hasn't estimates then show add new
    setIsAddNew(estimates.length === 0);
  }, [estimates.length]);

  const userHasEstimate = estimates.some(
    (estimate) => estimate.accountId === userId
  );
  // if user has estimate then show add new with no default and focus on assign to input
  // otherwise show add new with current user as default and focus on estimate input
  const defaultValuesForNewEstimate = userHasEstimate
    ? {}
    : {
        seconds: 0,
        assignToId: userId,
      };

  const handleSave = useCallback(
    async (data) => {
      const editableEstimate = estimates.find(
        (estimate) => estimate.id === editModeId
      );
      setEditModeId(null);
      setIsAddNew(false);

      // if estimate hasn't changed, don't save
      const changed = [
        editableEstimate && editableEstimate.accountId !== data.accountId,
        editableEstimate && editableEstimate.seconds !== data.seconds,
      ].some(Boolean);
      if (changed || isAddNew) {
        const body = {
          ...data,
          cardId: props.cardId,
          boardId,
        };
        if (props.handleSubmit) {
          props.handleSubmit(body);
        }
      }
    },
    [boardId, props.cardId, editModeId, isAddNew]
  );
  const handleDelete = useCallback(
    async (id) => {
      await props.deleteEstimate({ id });
      if (props.handleDelete) {
        props.handleDelete(id);
      }
      if (estimates.length === 1) {
        setIsAddNew(true);
      }
    },
    [estimates, props.deleteEstimate, props.handleDelete]
  );

  const hideAddNew = props.users.length === estimates.length;
  return (
    <S.EstimateForm paddingBottom={editModeId || isAddNew ? 150 : 0}>
      {estimates.map((estimate) => (
        <EstimateFormItem
          key={estimate.id}
          estimate={estimate}
          users={props.users}
          editMode={editModeId === estimate.id}
          showEditButtons={!(editModeId || isAddNew) && !props.isSaving}
          handleEditMode={() => {
            setEditModeId(estimate.id);
          }}
          handleDelete={handleDelete}
          handleSave={handleSave}
          filterOptions={(options) => {
            const userIds = estimates.map((e) => e.accountId);
            return options.filter(
              (option) =>
                !userIds.includes(option.value) ||
                option.value === estimate.accountId
            );
          }}
        />
      ))}
      {isAddNew && (
        <EstimateFormItem
          editMode
          users={props.users}
          defaultValues={defaultValuesForNewEstimate}
          handleEditMode={() => {
            setIsAddNew(false);
          }}
          handleSave={handleSave}
          filterOptions={(options) => {
            const userIds = estimates.map((e) => e.accountId);
            return options.filter((option) => !userIds.includes(option.value));
          }}
        />
      )}

      {hideAddNew ||
        (!isAddNew && !editModeId && (
          <Button
            withIcon
            onClick={() => {
              setIsAddNew(true);
            }}
            isDisabled={isLoading}
          >
            {isLoading ? (
              <Spinner size={['16px', '16px']} />
            ) : (
              <AddIcon
                size="large"
                shouldPadding
              />
            )}
          </Button>
        ))}
    </S.EstimateForm>
  );
};

EstimateForm.defaultProps = {
  estimates: [],
  users: [],
  isSaving: false,
};

EstimateForm.propTypes = {
  userId: PropTypes.string.isRequired,
  boardId: PropTypes.string.isRequired,
  isSaving: PropTypes.bool,
  handleSubmit: PropTypes.func,
  cardId: PropTypes.string,
  handleDelete: PropTypes.func,
  deleteEstimate: PropTypes.func,
  estimates: PropTypes.array,
  users: PropTypes.array,
};

export default EstimateForm;
